import {interpolateLinear} from '@jetbrains/ring-ui/components/global/linear-function'
import classNames from 'classnames'
import type {GraphNode as DagNode} from 'd3-dag/dist/graph'
import type {ReactNode} from 'react'
import React, {useMemo} from 'react'

import type {GraphNode} from '../../../utils/graph'
import {createLayout} from '../../../utils/graph'

import styles from './PipelineChart.css'

const FIRST_POINT_OFFSET_X = 27
const LAST_POINT_OFFSET_X = 2
type Props<T> = {
  className?: string
  graph: Record<string, GraphNode<T>>
  nodeWidth: number
  nodeHeight: number
  pointOffsetY: number
  renderJob: (data: DagNode<GraphNode<T>>) => ReactNode
}
export function PipelineChart<T>({
  className,
  graph,
  nodeWidth,
  nodeHeight,
  pointOffsetY,
  renderJob,
}: Props<T>) {
  const {width, height, dag} = useMemo(
    () => createLayout(graph, [nodeHeight, nodeWidth]),
    [graph, nodeHeight, nodeWidth],
  )
  const nodes = useMemo(() => Array.from(dag?.nodes() ?? []), [dag])

  return useMemo(
    () => (
      <div className={classNames(styles.wrapper, className)} style={{width: height, height: width}}>
        {nodes.map(renderJob)}
        <svg height={width} width={height} className={styles.edges}>
          {Array.from(dag?.links() ?? []).map(link => {
            const points = link.points.map(([x, y], i, array) => ({
              x:
                y! -
                nodeWidth / 2 +
                interpolateLinear(
                  FIRST_POINT_OFFSET_X,
                  LAST_POINT_OFFSET_X,
                  i / (array.length - 1),
                ),
              y: x! - nodeHeight / 2 + pointOffsetY,
            }))
            return (
              <path
                key={`${link.source.data.id} => ${link.target.data.id}`}
                // TODO make the lines pretty
                d={`M ${points[0].x} ${points[0].y} ${points
                  .slice(1)
                  .map((point, i) => {
                    const prevPoint = points[i]
                    const middleX = (prevPoint.x + point.x) / 2
                    return `C ${middleX} ${prevPoint.y}, ${middleX} ${point.y}, ${point.x} ${point.y}`
                  })
                  .join(' ')}`}
              />
            )
          })}
        </svg>
      </div>
    ),
    [className, dag, height, nodeHeight, nodeWidth, nodes, pointOffsetY, renderJob, width],
  )
}
